import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields, Discipline } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import MigrationHelper from '../MigrationHelper';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface ComponentAreaInfo {
  readonly componentFailureId?: number;
  readonly areaId: number;
  readonly areaPcsId: string;
  readonly inspectionId: number;
  readonly trafficSafety: number;
  readonly componentVersionId?: number;
}

export interface DeficiencyList {
  readonly pkDeficiencyUUID: string;
  readonly repairFactorId: number;
  readonly isCompletedDuringInspection: boolean;
  readonly componentFailureId: string;
  readonly componentType: string;
  readonly pcsId?: string;
  readonly remedialAction: string;
  readonly causeOfFailure: string;
  readonly remedialActionId: number;
  readonly commentRemedialAction: string;
  readonly assetCategoryComponentId: number;
  readonly commentAssetComponent: string;
  readonly assetInspectionId: number;
  readonly failureCauseCategory: number;
  readonly areaId?: number;
  readonly fkAsset?: number;
  readonly fkArea?: number;
  readonly isActive: boolean;
  readonly locationDescription: string;
  readonly commentObservation: string;
  readonly name: string;
  readonly dateObserved: string;
  readonly site: number;
  readonly discipline: number;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly isDelete: string;
  readonly fieldInspectorId: number;
  readonly leadInspectorId?: number;
  readonly inspectionTaskId: number;
  readonly commentSiteCoordinator?: string;
  readonly commentInspectorLead: string;
  readonly commentInspectorField: string;
  readonly attentionFlag: boolean;
  readonly taskStatusCategory?: number;
  readonly fkComponent: number;
  readonly lowerElevation: string;
  readonly upperElevation: string;
  readonly isAdhoc: boolean;
  readonly isReInspectedComponent: boolean;
  readonly componentAreaInfo: [ComponentAreaInfo];
  readonly inspectorOperationalDowntime: number;
  readonly inspectorConditionRating: number;
  readonly inspectorUrgency: number;
  readonly inspectorTrafficScore: number;
  readonly componentVersionInspectionId?: number;
  readonly componentVersionId?: number;
  readonly componentVersionUUID: string;
  role?: number;
}

export const deficiencyListSchema: RxJsonSchema<DeficiencyList> = {
  type: 'object',
  version: 2,
  description: 'describes deficiency lists',
  primaryKey: 'componentVersionUUID',
  properties: {
    pkDeficiencyUUID: { type: 'string', maxLength: 36 },
    componentFailureId: { type: 'string' },
    pcsId: { type: ['string', 'null'] },
    repairFactorId: { type: ['number', 'null'] },
    isCompletedDuringInspection: { type: ['boolean', 'null'] },
    componentType: { type: ['string', 'null'] },
    remedialAction: { type: ['string', 'null'] },
    causeOfFailure: { type: ['string', 'null'] },
    remedialActionId: { type: ['number', 'null'] },
    commentRemedialAction: { type: ['string', 'null'] },
    assetCategoryComponentId: { type: ['number', 'null'] },
    commentAssetComponent: { type: ['string', 'null'] },
    assetInspectionId: { type: ['number', 'null'] },
    failureCauseCategory: { type: ['number', 'null'] },
    areaId: { type: ['number', 'null'] },
    fkAsset: { type: ['number', 'null'] },
    fkArea: { type: ['number', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    isAdhoc: { type: ['boolean', 'null'] },
    locationDescription: { type: ['string', 'null'] },
    commentObservation: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    dateObserved: { type: ['string', 'null'] },
    site: { type: 'number' },
    discipline: { type: 'number' },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    isDelete: { type: ['string', 'null'] },
    fieldInspectorId: { type: ['number', 'null'] },
    leadInspectorId: { type: ['number', 'null'] },
    inspectionTaskId: { type: ['number', 'null'] },
    commentSiteCoordinator: { type: ['string', 'null'] },
    commentInspectorLead: { type: ['string', 'null'] },
    commentInspectorField: { type: ['string', 'null'] },
    attentionFlag: { type: ['boolean', 'null'] },
    isReInspectedComponent: { type: ['boolean', 'null'] },
    taskStatusCategory: { type: ['number', 'null'] },
    fkComponent: { type: ['number', 'null'] },
    lowerElevation: { type: ['string', 'null'] },
    upperElevation: { type: ['string', 'null'] },
    role: { type: ['number', 'null'] },
    componentAreaInfo: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          componentFailureId: { type: ['number', 'null'] },
          areaId: { type: ['number', 'null'] },
          areaPcsId: { type: ['string', 'null'] },
          inspectionId: { type: ['number', 'null'] },
          trafficSafety: { type: ['number', 'null'] },
        },
      },
    },
    inspectorOperationalDowntime: { type: ['number', 'null'] },
    inspectorConditionRating: { type: ['number', 'null'] },
    inspectorUrgency: { type: ['number', 'null'] },
    inspectorTrafficScore: { type: ['number', 'null'] },
    componentVersionId: { type: ['number', 'null'] },
    componentVersionInspectionId: { type: ['number', 'null'] },
    componentVersionUUID: { type: 'string', maxLength: 36 },
  },
};

export const deficiencyListMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: DeficiencyList) => {
    const newDoc = {
      role: 7,
      ...oldDoc,
    };
    return newDoc;
  },
  2: (oldDoc: DeficiencyList) => {
    return MigrationHelper.instance.migrateDeficiencyV2(oldDoc).then(response => {
      if (response && response.componentVersionUUID) return { ...oldDoc, ...response };
      // if componentVersionUUID is not present, do not migrate data
      return null;
    });
  },
};

export type DeficiencyListCollection = RxCollection<DeficiencyList> | null;
export type DeficiencyListDocument = RxDocument<DeficiencyList>;

export const deficiencyListPullQueryBuilder = (document: DeficiencyList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query deficiencyListFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime
      $isLeadInspector: Boolean!
    ) {
      deficiencyListFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        pkDeficiencyUUID
        repairFactorId
        componentFailureId
        isCompletedDuringInspection
        componentType
        causeOfFailure
        remedialAction
        remedialActionId
        commentRemedialAction
        assetCategoryComponentId
        commentAssetComponent
        assetInspectionId
        failureCauseCategory
        areaId
        fkAsset
        fkArea
        pcsId
        isActive
        isAdhoc
        locationDescription
        commentObservation
        name
        dateObserved
        site
        discipline
        dateCreated
        dateModified
        isDelete
        fieldInspectorId
        leadInspectorId
        inspectionTaskId
        commentSiteCoordinator
        commentInspectorLead
        commentInspectorField
        attentionFlag
        taskStatusCategory
        fkComponent
        lowerElevation
        isReInspectedComponent
        upperElevation
        componentVersionId
        inspectorOperationalDowntime
        inspectorConditionRating
        inspectorUrgency
        inspectorTrafficScore
        componentVersionInspectionId
        componentVersionUUID
        componentAreaInfo {
          componentFailureId
          areaId
          areaPcsId
          inspectionId
          trafficSafety
          componentVersionId
        }
      }
    }
  `);

  const variables = {
    siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
    disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
    lastRequested: getDateModified(
      doc?.dateModified,
      doc?.dateCreated,
      doc.site,
      doc.discipline,
      JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
    ),
    isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
  };

  return {
    query,
    variables,
  };
};

export const deficiencyListPushQueryBuilder = (doc: DeficiencyListDocument[]) => {
  const document = doc[0];
  const query =
    document.isDelete === 'False' || !!document.componentVersionInspectionId
      ? print(gql`
          mutation mobileAddOrUpdateDeficiency(
            $role: Int!
            $MobileInspectionDeficienciesListFeedInput: MobileInspectionDeficienciesListFeedInput
          ) {
            mobileAddOrUpdateDeficiency(
              role: $role
              deficiency: $MobileInspectionDeficienciesListFeedInput
            ) {
              assetInsepctionComponentFailureId
              deficiencyUUID
              status
              componentVersionId
            }
          }
        `)
      : print(gql`
          mutation mobileDeleteDeficiency(
            $role: Int!
            $MobileInspectionDeficienciesListFeedInput: MobileInspectionDeficienciesListFeedInput
          ) {
            mobileDeleteDeficiency(
              role: $role
              deficiency: $MobileInspectionDeficienciesListFeedInput
            ) {
              assetInsepctionComponentFailureId
              deficiencyUUID
              status
            }
          }
        `);
  const { role, ...data } = document;
  const variables = {
    role: role ? role : getRoleForMutation(data),
    MobileInspectionDeficienciesListFeedInput: data,
  };

  return {
    query,
    variables,
  };
};

export const deficiencyListPushModifier = (doc: DeficiencyListDocument) => {
  const obj: AdditionalFields & { assetCategoryComponentId?: number | null } = {
    ...doc,
    assetCategoryComponentId:
      doc.discipline === Discipline.Shaft ? null : doc.assetCategoryComponentId,
  };
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'dateCreated',
    'dateModified',
    '_meta',
    'isDeleted',
    'role',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });
  return obj;
};

export default {};
